import React, { FC } from 'react';
import classNames from 'classnames';
import { Box, Avatar, Typography, makeStyles } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { CustomTheme } from 'common/ui/interfaces';
import { IEmployeeBasicInfo } from 'interfaces/employee.interfaces';
import { ReactComponent as Incognito } from 'img/spy.svg';
import profilePlaceholder from 'img/photo-placeholder.png';
import { getInitials } from 'helpers/common';
import { getString } from 'common/utils/typeUtils';
import { makeEmployeeLabel } from 'utils/employee.utils';
import messages from './messages';

export const userAutocompleteIconAvatarStyles = (theme: CustomTheme): any => ({
  height: theme.spacing(2),
  width: theme.spacing(2),
  marginRight: theme.spacing(1),
});

export const userAutocompleteIconTextStyles = (): any => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const useStyles = makeStyles((theme: CustomTheme) => ({
  item: {
    position: 'absolute',
    display: 'inline-flex',
    bottom: 10,
    left: 14,
    fontSize: theme.typography.h5.fontSize,
    width: 'calc(100% - 42px)',
  },
  iconAvatar: userAutocompleteIconAvatarStyles(theme),
  avatarWrap: {
    position: 'relative',
    flex: 'none',
    borderRadius: '50%',
    backgroundColor: theme.palette.secondary.light,
  },
  avatarInitial: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    fontSize: 8,
    fontWeight: 700,
    color: theme.palette.darkBackground?.light,
  },
  avatar: {
    width: '100%',
    height: '100%',
    borderRadius: '3px',
  },
  itemText: userAutocompleteIconTextStyles(),
}));

interface ISelectedUserOption {
  selectedValue: any;
  hideNOS: boolean;
}

const SelectedUserOption: FC<ISelectedUserOption> = ({ hideNOS, selectedValue }) => {
  const classes = useStyles();
  const firstName = getString(selectedValue?.firstName);
  const lastName = getString(selectedValue?.lastName);
  const initials = getInitials(firstName, lastName);
  let employeeBasicInfo: IEmployeeBasicInfo | null = null;

  if (typeof selectedValue?.id === 'number' && firstName && lastName) {
    employeeBasicInfo = {
      id: selectedValue.id,
      firstName,
      lastName,
      employeeIdentifier: getString(selectedValue.employeeIdentifier) || null,
      imageUrl: getString(selectedValue.imageUrl) || null,
      active: !!selectedValue.active,
    };
  }

  if (hideNOS && !selectedValue) {
    return <Box className={classes.item} />;
  }

  if (!selectedValue) {
    return (
      <Box className={classes.item}>
        <Incognito className={classes.iconAvatar} />
        <FormattedMessage {...messages.notInSystem} />
      </Box>
    );
  }

  return (
    <Box className={classes.item}>
      <Box className={classNames(classes.avatarWrap, classes.iconAvatar)}>
        {initials && !selectedValue.imageUrl ? (
          <Box className={classes.avatarInitial}>{initials}</Box>
        ) : (
          <Avatar
            src={selectedValue.imageUrl || profilePlaceholder}
            alt="avatar-icon"
            className={classes.avatar}
          />
        )}
      </Box>

      <Typography align="center" className={classes.itemText}>
        {employeeBasicInfo ? makeEmployeeLabel(employeeBasicInfo) : `${firstName} ${lastName}`}
      </Typography>
    </Box>
  );
};

export default React.memo(SelectedUserOption);
